import React, { Component, ReactNode } from 'react';
import { ui } from '../utils';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('React Error Boundary caught an error:', error, errorInfo);

    void fetch('/api/client-errors', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        url: window.location.href,
        userAgent: window.navigator.userAgent,
        timestamp: new Date().toISOString(),
      }),
      keepalive: true,
    }).catch((reportError) => {
      console.warn('Failed to report client error:', reportError);
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex h-full items-center justify-center bg-background px-6 text-foreground">
          <div className="w-full max-w-md rounded-[2rem] border border-border bg-card px-8 py-10 text-center shadow-2xl shadow-black/10">
            <h1 className="mb-4 text-2xl font-bold text-foreground-bright">Something went wrong</h1>
            <p className="mb-6 text-muted-foreground">
              {this.state.error?.message || 'An unexpected error occurred while loading the application.'}
            </p>
            <button
              onClick={() => window.location.reload()}
              className={`${ui.button.base} ${ui.button.primary} px-4 py-2.5`}
            >
              Reload Application
            </button>
            <div className="mt-4">
              <details className="text-left">
                <summary className="cursor-pointer text-muted-foreground transition-colors duration-150 hover:text-foreground">
                  Technical Details
                </summary>
                <pre className="mt-2 max-h-32 overflow-auto rounded-xl border border-border bg-background p-3 text-xs text-muted-foreground">
                  {this.state.error?.stack || 'No stack trace available'}
                </pre>
              </details>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
